<template>
  <b-modal
    ref="userCalendar"
    size="xl"
    static
    :title="$t('global.userCalendar')"
    centered
    modal-class="user-calendar-modal"
    hide-footer
    @shown="updateCalendarSize"
  >
    <b-container id="container">
      <b-row>
        <b-col cols="3">
          <div
            v-if="unconfirmedEvents.length"
            class="d-flex justify-content-between"
          >
            <span class="main">{{ $t('component.calendar.pendingToValidate') }} ({{ unconfirmedEvents.length }})</span>
            <b-button
              class="btn__icon btn__icon--primary px-2 py-0"
              variant="link"
              @click="$refs.modalEventCalendarWrapper.show()"
            >
              <i
                v-tooltip="$t('component.calendar.newRequest')"
                class="far fa-plus"
                aria-hidden="true"
              />
            </b-button>
          </div>
          <div
            v-if="unconfirmedEvents.length"
            class="events-column-height pr-2"
          >
            <div
              v-for="(event, index) in unconfirmedEvents"
              :key="index"
              class="event-card py-2 px-3 mt-2"
              @click="$refs.modalEventCalendarWrapper.show([event])"
            >
              <div>
                <span
                  class="event-title"
                  :class="EVENT_TYPE[event.type.code].fontColor"
                >{{ event.observations }}</span>
              </div>
              <div>
                <i
                  class="fal fa-calendar-alt mr-2"
                  aria-hidden="true"
                />
                <span class="event-date">{{ event.startingDate | date }}</span>
                <template v-if="event.endingDate">
                  <span class="event-date"> {{ $t('component.calendar.to') }} </span>
                  <span class="event-date">{{ event.endingDate | date }}</span>
                </template>
              </div>
            </div>
          </div>
          <div
            v-if="!unconfirmedEvents.length"
            class="empty-state"
          >
            <div
              class="d-flex align-items-center justify-content-center"
              style="height: 150px;position:relative"
            >
              <div class="calendar-icon">
                <calendar />
              </div>
              <div class="circle" />
            </div>
            <div class="mt-4 d-flex justify-content-center">
              <b-button
                class="btn__outline"
                @click="$refs.modalEventCalendarWrapper.show()"
              >
                <i
                  class="far fa-calendar-plus mr-2"
                  aria-hidden="true"
                />
                {{ $t('component.calendar.newRequest') }}
              </b-button>
            </div>
          </div>
        </b-col>
        <b-col cols="9">
          <div class="calendar-height">
            <FullCalendar
              ref="userCalendarWindow"
              default-view="dayGridMonth"
              :locale="initialLocale"
              :locales="allLocales"
              :first-day="1"
              :height="'parent'"
              :plugins="calendarPlugins"
              :fixed-week-count="false"
              :header="calendarHeader"
              :selectable="true"
              :events="events"
              @datesRender="onDatesRender($event)"
              @select="onDateClick"
              @eventClick="onEventClick"
            />
          </div>
          <div class="calendar-footer d-flex mt-3">
            <div class="mr-4">
              <i
                class="fas fa-circle mr-2 holiday-color legend-circle"
                aria-hidden="true"
              />
              <span class="dark">{{ $t('component.calendar.holidays') }}</span>
            </div>
            <div class="mr-4">
              <i
                class="fas fa-circle mr-2 absence-color legend-circle"
                aria-hidden="true"
              />
              <span class="dark">{{ $tc('component.calendar.absence', 2) }}</span>
            </div>
            <div class="mr-4">
              <i
                class="fas fa-circle mr-2 sick-color legend-circle"
                aria-hidden="true"
              />
              <span class="dark">{{ $tc('component.calendar.leave', 2) }}</span>
            </div>
            <div class="mr-4">
              <i
                class="fas fa-circle mr-2 festive-color legend-circle"
                aria-hidden="true"
              />
              <span class="dark">{{ $t('component.calendar.publicHoliday') }}</span>
            </div>
            <div class="mr-4">
              <i
                class="fas fa-circle mr-2 day-off-color legend-circle"
                aria-hidden="true"
              />
              <span class="dark">{{ $t('component.calendar.noWorkingDays') }}</span>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
    <modal-wrapper ref="modalEventCalendarWrapper">
      <modal-event-calendar
        ref="modalEventCalendar"
        @createEvent="createLoggedInPersonnelEvent($event)"
        @updateEvent="updateLoggedInPersonnelEvent($event)"
        @deleteEvent="deleteLoggedInPersonnelEvent($event)"
      />
    </modal-wrapper>
  </b-modal>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import allLocales from '@fullcalendar/core/locales-all'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import ModalEventCalendar from '@/components/userConfiguration/ModalEventCalendar'
import Calendar from '@/assets/images/svg/Calendar'
import LoggedInPersonnelEventsServices
    from '@/api/resources/personnelEvents/loggedInPersonnelEvents/loggedInPersonnelEventsServices'
import { EVENT_TYPE } from '@/api/resources/personnelEvents/constants/eventType'
import moment from 'moment'
import LaboratoryWeekendDaysService from '@/api/resources/common/laboratoryWeekendDaysService'
import { DAY_OF_WEEK } from '@/api/resources/personnelEvents/constants/dayOfWeek'
import LaboratoryHolidaysService from '@/api/resources/common/laboratoryHolidaysService'
import sessionStore from '@/session/sessionStore'
import { DateUtils } from '@/shared/util/date-utils'

export default {
    name: 'UserCalendar',
    components: {
        FullCalendar,
        ModalEventCalendar,
        Calendar
    },
    data() {
        return {
            initialLocale: sessionStore.getLocale(),
            allLocales: allLocales,
            unconfirmedEvents: [],
            calendarApi: null,
            calendarPlugins: [ dayGridPlugin, listPlugin, interactionPlugin ],
            calendarHeader: {
                center: 'title',
                left: 'today prev,next',
                right: 'dayGridMonth,dayGridWeek,dayGridDay,listYear'
            },
            userEvents: [],
            publicHolidays: [],
            weekendDays: []
        }
    },
    computed: {
        EVENT_TYPE() {
            return EVENT_TYPE
        },
        events() {
            return this.userEvents.concat(this.weekendDays.concat(this.publicHolidays))
        },
        laboratoryId() {
            return this.$store.getters.labId
        }
    },
    mounted() {
        this.calendarApi = this.$refs.userCalendarWindow.getApi()
    },
    methods: {
        async show() {
            await this.getAllUnconfirmedLoggedInPersonnelEvents()
            await this.getAllLaboratoryWeekendDays()
            this.calendarApi.setOption('locale', sessionStore.getLocale())
            this.$refs.userCalendar.show()
        },
        hide() {
            this.$refs.userCalendar.hide()
        },
        onDatesRender(data) {
            this.currentDatesRange = { activeStart: data.view.activeStart, activeEnd: data.view.activeEnd }
            this.getAllLoggedInPersonnelEventsByDateRange()
            this.getAllLaboratoryHolidaysByDateRange()
        },
        onEventClick(clickedEvent) {
            const mappedEvent = {
                id: clickedEvent.event.extendedProps.personnelEventId,
                startingDate: clickedEvent.event.start,
                endingDate: moment(clickedEvent.event.end).subtract(1, 'days'),
                observations: clickedEvent.event.extendedProps.observations,
                type: EVENT_TYPE[clickedEvent.event.extendedProps.type]
            }
            this.$refs.modalEventCalendarWrapper.show([mappedEvent])
        },
        onDateClick(dateSelection) {
            const mappedEvent = {
                startingDate: dateSelection.startStr,
                endingDate: moment(dateSelection.endStr).subtract(1, 'days')
            }
            this.$refs.modalEventCalendarWrapper.show([mappedEvent])
        },
        updateCalendarSize() {
            this.calendarApi.updateSize()
        },
        mapEvents(events, type) {
            let mappedEvents = []
            for (let event of events) {
                mappedEvents.push({
                    title: event.observations ? event.observations : event.name,
                    start: event.startingDate,
                    end: DateUtils.formatDate(moment(event.endingDate).add(1, 'days').toDate()),
                    editable: (!type && !event.confirmationDate),
                    allDay: true,
                    classNames: type ? EVENT_TYPE[type].classNames : EVENT_TYPE[event.type.code].classNames,
                    displayEventTime: false,
                    fontColor: type ? EVENT_TYPE[type].fontColor : EVENT_TYPE[event.type.code].fontColor,
                    extendedProps: {
                        type: event.type ? event.type.code : type,
                        observations: event.observations,
                        personnelEventId: event.id
                    }
                })
            }
            return mappedEvents
        },
        async getAllUnconfirmedLoggedInPersonnelEvents() {
            this.updateLoading(true)
            const [error, response] = await this.toCall(LoggedInPersonnelEventsServices.getAllUnconfirmedLoggedInPersonnelEvents())
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            this.unconfirmedEvents = response
        },
        async getAllLoggedInPersonnelEventsByDateRange() {
            this.updateLoading(true)
            const params = {
                from: DateUtils.formatDate(this.currentDatesRange.activeStart),
                to: DateUtils.formatDate(this.currentDatesRange.activeEnd)
            }
            const [error, response] = await this.toCall(LoggedInPersonnelEventsServices.getAllLoggedInPersonnelEventsByDateRange(params))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            this.userEvents = this.mapEvents(response)
        },
        async createLoggedInPersonnelEvent(event) {
            this.updateLoading(true)
            const request = {
                endingDate: DateUtils.formatDate(event.endDateSelected),
                observations: event.observations,
                startingDate: DateUtils.formatDate(event.startDateSelected),
                type: event.eventTypeSelected.code
            }
            const [error] = await this.toCall(LoggedInPersonnelEventsServices.createLoggedInPersonnelEvent(request))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            await this.reloadCalendarData()
            this.showToastSuccessGlobal()
        },
        async updateLoggedInPersonnelEvent(event) {
            this.updateLoading(true)
            const request = {
                endingDate: DateUtils.formatDate(event.endDateSelected),
                observations: event.observations,
                startingDate: DateUtils.formatDate(event.startDateSelected),
                type: event.eventTypeSelected.code
            }
            const [error] = await this.toCall(LoggedInPersonnelEventsServices.updateLoggedInPersonnelEvent(request, event.selectedEventId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            await this.reloadCalendarData()
            this.showToastSuccessGlobal()
        },
        async deleteLoggedInPersonnelEvent(personnelEvents) {
            const deleteConfirmation = await this.showConfirmationModalGlobal()
            if (deleteConfirmation) {
                let batchError = false
                this.updateLoading(true)
                for (let eventToDelete of personnelEvents) {
                    const [error] = await this.toCall(LoggedInPersonnelEventsServices.deleteLoggedInPersonnelEvent(eventToDelete.selectedEventId))
                    if (error) {
                        this.showErrorModalGlobalFromErrorResponse(error)
                        batchError = true
                    }
                }
                if (!batchError) {
                    this.showToastSuccessGlobal(this.$tc('configuration.modal.calendar.onEventDeletedMsg', 2))
                }
                await this.reloadCalendarData()
                this.updateLoading(false)
            }
        },
        async getAllLaboratoryWeekendDays() {
            this.updateLoading(true)
            const [error, response] = await this.toCall(LaboratoryWeekendDaysService.getAllLaboratoryWeekendDays(this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            let daysOfWeek = []
            for (let weekendDay of response) {
                const dayOfTheWeekCode = weekendDay.dayOfTheWeek.code
                daysOfWeek.push(DAY_OF_WEEK[dayOfTheWeekCode].arrayPosition)
            }
            this.weekendDays = [{
                title: this.$t('configuration.modal.calendar.workDays'),
                classNames: 'day-off',
                rendering: 'background',
                daysOfWeek: daysOfWeek
            }]
        },
        async getAllLaboratoryHolidaysByDateRange() {
            this.updateLoading(true)
            const params = {
                from: DateUtils.formatDate(this.currentDatesRange.activeStart),
                to: DateUtils.formatDate(this.currentDatesRange.activeEnd)
            }
            const [error, response] = await this.toCall(LaboratoryHolidaysService.getAllLaboratoryHolidaysByDateRange(params, this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            this.publicHolidays = this.mapEvents(response, EVENT_TYPE.PUBLIC_HOLIDAY.code)
        },
        async reloadCalendarData() {
            await this.getAllLoggedInPersonnelEventsByDateRange()
            await this.getAllUnconfirmedLoggedInPersonnelEvents()
        }
    }
}
</script>

<style lang="scss">
    @import '~@fullcalendar/core/main.css';
    @import '~@fullcalendar/daygrid/main.css';
    @import '~@fullcalendar/list/main.css';
    .user-calendar-modal .modal-body {
        border-radius: .25rem;
    }
    .user-calendar-modal .calendar-height {
        height: 500px;
    }
    .user-calendar-modal .calendar-icon {
        width: 100px;
    }
    .user-calendar-modal .calendar-icon .cls-1,
    .user-calendar-modal .calendar-icon .cls-2 {
        fill: #000 !important;
    }
    .user-calendar-modal .calendar-icon svg {
        opacity: .4;
    }
    .user-calendar-modal .empty-state .circle {
        position: absolute;
        width: 130px;
        height: 130px;
        background: vt-color(primary);
        opacity: .15;
        border-radius: 50%;
        z-index: 0;
    }
    .user-calendar-modal .event-card {
        border: 1px solid vt-color(general-border);
        box-shadow: 0 1px 5px 0 #9c9c9c;
        cursor: pointer;
    }
    .user-calendar-modal .event-card:hover {
        box-shadow: 0 1px 7px 0 #9c9c9c;
    }
    .user-calendar-modal .event-card .event-title {
        font-weight: bold;
    }
    .user-calendar-modal .event-card .event-date {
        font-size: font-size(very-small);
    }
    .user-calendar-modal .events-column-height {
        height: calc(100vh - 460px);
        overflow-y: auto;
    }
    .user-calendar-modal .calendar-footer {
        text-transform: capitalize;
    }

    @media (max-width: 1300px) {
        .user-calendar-modal .events-column-height {
            height: calc(100vh - 390px);
        }
    }

    @media only screen and (max-width: 1366px) and (min-width: 1360px) {
        .user-calendar-modal .calendar-height {
            height: calc(100vh - 250px);
        }
        .user-calendar-modal .events-column-height {
            height: calc(100vh - 250px);
        }
    }
</style>
